import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Grid,
  GridItem,
  useColorModeValue,
  Button,
  Select,
  HStack,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Heading,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import { FaChartBar, FaUsers, FaCalendarAlt, FaMoneyBillWave, FaFileAlt, FaUserPlus, FaTimes } from "react-icons/fa";
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { getOverviewStats, getAppointmentStats, getRevenueStats, getConsultantStats, getRecentActivity } from '../services/reportService';

function Reports() {
  const textColor = useColorModeValue("gray.700", "white");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  
  // State برای فیلترها
  const [dateRange, setDateRange] = useState('month');
  const [reportType, setReportType] = useState('overview');
  
  // React Query hooks
  const { user } = useAuth();
  
  // دریافت آمار کلی
  const {
    data: overviewData,
    isLoading: overviewLoading,
    error: overviewError,
    refetch: refetchOverview
  } = useQuery(
    ['reports-overview', dateRange],
    () => getOverviewStats(dateRange),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 دقیقه
    }
  );

  // دریافت آمار ملاقات‌ها
  const {
    data: appointmentData,
    isLoading: appointmentLoading,
    refetch: refetchAppointments
  } = useQuery(
    ['reports-appointments', dateRange],
    () => getAppointmentStats(dateRange),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    }
  );

  // دریافت آمار درآمد
  const {
    data: revenueData,
    isLoading: revenueLoading,
    refetch: refetchRevenue
  } = useQuery(
    ['reports-revenue', dateRange],
    () => getRevenueStats(dateRange),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    }
  );

  // دریافت آمار مشاوران
  const {
    data: consultantData,
    isLoading: consultantLoading,
    refetch: refetchConsultants
  } = useQuery(
    ['reports-consultants', dateRange],
    () => getConsultantStats(dateRange),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    }
  );

  // دریافت فعالیت‌های اخیر
  const {
    data: activityData,
    isLoading: activityLoading,
    refetch: refetchActivity
  } = useQuery(
    ['reports-activity'],
    () => getRecentActivity(10),
    {
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000, // 2 دقیقه
    }
  );

  const isLoading = overviewLoading || appointmentLoading || revenueLoading || consultantLoading || activityLoading;
  const error = overviewError;
  
  // تابع refetch برای تلاش مجدد
  const refetch = () => {
    refetchOverview();
    refetchAppointments();
    refetchRevenue();
    refetchConsultants();
    refetchActivity();
  };

  // اگر در حال بارگذاری است
  if (isLoading) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Flex justify="center" align="center" minH="400px">
              <Spinner size="xl" color="teal.500" />
              <Text mr={4} fontSize="lg" color={textColor}>
                در حال بارگذاری گزارشات...
              </Text>
            </Flex>
          </Box>
        </Box>
      </Flex>
    );
  }

  // اگر خطا رخ داده است
  if (error) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertTitle>خطا در بارگذاری داده‌ها!</AlertTitle>
              <AlertDescription>
                {error.message || 'خطا در ارتباط با سرور'}
              </AlertDescription>
            </Alert>
            <Button 
              mt={4} 
              colorScheme="teal" 
              onClick={() => refetch()}
              isLoading={isLoading}
            >
              تلاش مجدد
            </Button>
          </Box>
        </Box>
      </Flex>
    );
  }

  const overview = overviewData?.data || {};
  const appointments = appointmentData?.data || {};
  const revenue = revenueData?.data || {};
  const consultants = consultantData?.data || {};
  const activities = activityData?.data || [];

  return (
    <Flex direction="column"  dir="rtl">
      <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
        <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
          <Box p="20px 0px 35px 0px">
            <Flex direction="column">
              <Text fontSize="lg" color={textColor} fontWeight="bold" me="auto">
                گزارشات و آمار
              </Text>
              <Text fontSize="md" fontWeight="400" me="auto" color="gray.400">
                مشاهده آمار و گزارشات کلینیک
              </Text>
            </Flex>
          </Box>
          
          {/* فیلترها */}
          <Box px="25px" mb="25px">
            <HStack spacing="15px" flexWrap="wrap">
              <Select
                placeholder="انتخاب بازه زمانی"
                borderRadius="15px"
                fontSize="sm"
                maxW="200px"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
              >
                <option value="week">هفته جاری</option>
                <option value="month">ماه جاری</option>
                <option value="quarter">سه ماهه</option>
                <option value="year">سال جاری</option>
              </Select>
              <Select
                placeholder="نوع گزارش"
                borderRadius="15px"
                fontSize="sm"
                maxW="200px"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
              >
                <option value="overview">نمای کلی</option>
                <option value="appointments">ملاقات‌ها</option>
                <option value="revenue">درآمد</option>
                <option value="consultants">مشاوران</option>
              </Select>
              <Button
                leftIcon={<FaChartBar />}
                colorScheme="teal"
                borderRadius="15px"
                fontSize="sm"
                fontWeight="bold"
                px="20px"
                py="10px"
                onClick={() => window.print()}
              >
                چاپ گزارش
              </Button>
            </HStack>
          </Box>

          {/* آمار کلی */}
          <Box px="25px" mb="25px">
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={6}>
              <GridItem>
                <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="15px" p="20px">
                  <Stat>
                    <StatLabel color="blue.600" fontSize="sm" fontWeight="bold">
                      <HStack spacing={2}>
                        <FaUsers />
                        <Text>کل مراجعان</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="blue.700" fontSize="2xl">
                      {overview?.totalClients?.toLocaleString('fa-IR') || '0'}
                    </StatNumber>
                    <StatHelpText color="blue.600">
                      <StatArrow type="increase" />
                      {overview?.clientGrowth || 0}%
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="15px" p="20px">
                  <Stat>
                    <StatLabel color="green.600" fontSize="sm" fontWeight="bold">
                      <HStack spacing={2}>
                        <FaCalendarAlt />
                        <Text>کل ملاقات‌ها</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="green.700" fontSize="2xl">
                      {overview?.totalAppointments?.toLocaleString('fa-IR') || '0'}
                    </StatNumber>
                    <StatHelpText color="green.600">
                      <StatArrow type="increase" />
                      {overview?.appointmentGrowth || 0}%
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg="purple.50" border="1px solid" borderColor="purple.200" borderRadius="15px" p="20px">
                  <Stat>
                    <StatLabel color="purple.600" fontSize="sm" fontWeight="bold">
                      <HStack spacing={2}>
                        <FaUsers />
                        <Text>کل مشاوران</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="purple.700" fontSize="2xl">
                      {overview?.totalConsultants?.toLocaleString('fa-IR') || '0'}
                    </StatNumber>
                    <StatHelpText color="purple.600">
                      فعال
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="15px" p="20px">
                  <Stat>
                    <StatLabel color="orange.600" fontSize="sm" fontWeight="bold">
                      <HStack spacing={2}>
                        <FaMoneyBillWave />
                        <Text>کل درآمد</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="orange.700" fontSize="2xl">
                      {overview?.totalRevenue?.toLocaleString('fa-IR') || '0'} تومان
                    </StatNumber>
                    <StatHelpText color="orange.600">
                      <StatArrow type="increase" />
                      {overview?.revenueGrowth || 0}%
                    </StatHelpText>
                  </Stat>
                </Box>
              </GridItem>
            </Grid>
          </Box>

          {/* جزئیات ملاقات‌ها */}
          <Box px="25px" mb="25px">
            <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
              <Box mb="20px">
                <Heading size="md" color={textColor}>وضعیت ملاقات‌ها</Heading>
              </Box>
              <Box>
                <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4}>
                  <Box textAlign="center">
                    <Badge colorScheme="green" fontSize="lg" p={3} borderRadius="lg">
                      {appointments?.confirmed || 0} تأیید شده
                    </Badge>
                  </Box>
                  <Box textAlign="center">
                    <Badge colorScheme="yellow" fontSize="lg" p={3} borderRadius="lg">
                      {appointments?.pending || 0} در انتظار
                    </Badge>
                  </Box>
                  <Box textAlign="center">
                    <Badge colorScheme="blue" fontSize="lg" p={3} borderRadius="lg">
                      {appointments?.completed || 0} انجام شده
                    </Badge>
                  </Box>
                  <Box textAlign="center">
                    <Badge colorScheme="red" fontSize="lg" p={3} borderRadius="lg">
                      {appointments?.cancelled || 0} لغو شده
                    </Badge>
                  </Box>
                </Grid>
              </Box>
            </Box>
          </Box>

          {/* برترین مشاوران */}
          <Box px="25px" mb="25px">
            <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
              <Box mb="20px">
                <Heading size="md" color={textColor}>برترین مشاوران</Heading>
              </Box>
              <Box>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th color="gray.400">رتبه</Th>
                      <Th color="gray.400">نام مشاور</Th>
                      <Th color="gray.400">تعداد ملاقات</Th>
                      <Th color="gray.400">درآمد</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {consultants?.topConsultants?.map((consultant, index) => (
                      <Tr key={index}>
                        <Td>
                          <Badge colorScheme={index === 0 ? "gold" : index === 1 ? "gray" : index === 2 ? "orange" : "blue"}>
                            {index + 1}
                          </Badge>
                        </Td>
                        <Td fontWeight="bold">{consultant.name}</Td>
                        <Td>{consultant.appointments}</Td>
                        <Td>{consultant.revenue?.toLocaleString('fa-IR')} تومان</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          </Box>

          {/* فعالیت‌های اخیر */}
          <Box px="25px">
            <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
              <Box mb="20px">
                <Heading size="md" color={textColor}>فعالیت‌های اخیر</Heading>
              </Box>
              <Box>
                <VStack spacing={3} align="stretch">
                  {activities?.map((activity, index) => {
                    // انتخاب آیکون مناسب بر اساس نوع فعالیت
                    let icon = FaFileAlt;
                    let iconColor = "teal";
                    
                    switch (activity.type) {
                      case 'appointment':
                        icon = FaCalendarAlt;
                        iconColor = "blue";
                        break;
                      case 'client':
                        icon = FaUserPlus;
                        iconColor = "green";
                        break;
                      case 'consultant':
                        icon = FaUsers;
                        iconColor = "purple";
                        break;
                      case 'cancellation':
                        icon = FaTimes;
                        iconColor = "red";
                        break;
                      default:
                        icon = FaFileAlt;
                        iconColor = "teal";
                    }
                    
                                         const IconComponent = icon;
                     return (
                       <Flex key={index} justify="space-between" align="center" p={3} bg="gray.50" borderRadius="md">
                         <HStack spacing={3}>
                           <IconComponent color={iconColor} />
                           <Text fontSize="sm">{activity.message}</Text>
                         </HStack>
                         <Text fontSize="xs" color="gray.500">
                           {new Date(activity.date).toLocaleDateString('fa-IR')}
                         </Text>
                       </Flex>
                     );
                  })}
                </VStack>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Flex>
  );
}

export default Reports; 